import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { leadUploadService } from "../../services/leadUploadService";
import { campaignTemplateService } from "../../services/campaignTemplateService";
import { LeadUpload, ExtractedData } from "../../types/leadUpload";
import { useToast } from "../ui/use-toast";

interface ManualReviewFormProps {
  leadUpload: LeadUpload;
  onComplete: () => void;
}

export function ManualReviewForm({
  leadUpload,
  onComplete,
}: ManualReviewFormProps) {
  const extractedData = (leadUpload.extracted_data as ExtractedData) || {};
  const [formData, setFormData] = useState<ExtractedData>({
    first_name:
      extractedData.first_name || extractedData.name?.split(" ")[0] || "",
    last_name:
      extractedData.last_name ||
      extractedData.name?.split(" ").slice(1).join(" ") ||
      "",
    email: extractedData.email || "",
    phone: extractedData.phone || "",
    ...extractedData,
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.phone) {
        toast({
          title: "Phone number required",
          description: "Please enter a phone number to create the lead",
          variant: "destructive",
        });
        return;
      }

      console.log("Creating lead from upload with manual data:", {
        uploadId: leadUpload.id,
        formData: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          // Don't log all data for privacy
        },
        hasCampaignTemplateId: !!leadUpload.campaign_template_id,
        campaignTemplateId: leadUpload.campaign_template_id,
        organizationId: leadUpload.organization_id,
      });

      // Create the lead from the upload with manual data
      const { success, leadId, error } =
        await leadUploadService.createLeadFromUpload(leadUpload.id, formData);

      console.log("Lead creation result:", {
        success,
        leadId,
        hasError: !!error,
      });

      // If the lead was created successfully and there's a campaign_template_id, add the lead to the campaign
      if (success && leadId && leadUpload.campaign_template_id) {
        console.log(
          `Adding lead ${leadId} to campaign template ${leadUpload.campaign_template_id}`,
        );
        try {
          const result =
            await campaignTemplateService.addLeadsToCampaignTemplate(
              leadUpload.campaign_template_id,
              [leadId],
            );
          console.log("Result of adding lead to campaign template:", result);
        } catch (campaignError) {
          console.error("Error adding lead to campaign template:", campaignError);
          // Continue even if there's an error adding to campaign template
        }
      }

      if (error) {
        throw error;
      }

      toast({
        title: "Lead created successfully",
        description: "The lead has been created from the uploaded file",
      });

      onComplete();
    } catch (error) {
      console.error("Error creating lead:", error);
      toast({
        title: "Error creating lead",
        description:
          error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email || ""}
          onChange={handleChange}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="flex items-center">
          Phone Number <span className="text-danger ml-1">*</span>
        </Label>
        <Input
          id="phone"
          name="phone"
          value={formData.phone || ""}
          onChange={handleChange}
          required
        />
      </div>

      {formData.skills && (
        <div className="space-y-2">
          <Label htmlFor="skills">Skills</Label>
          <Textarea
            id="skills"
            name="skills"
            value={
              Array.isArray(formData.skills)
                ? formData.skills.join(", ")
                : formData.skills
            }
            onChange={handleChange}
            rows={2}
          />
        </div>
      )}

      {formData.experience && (
        <div className="space-y-2">
          <Label htmlFor="experience">Experience</Label>
          <Textarea
            id="experience"
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            rows={3}
          />
        </div>
      )}

      {formData.education && (
        <div className="space-y-2">
          <Label htmlFor="education">Education</Label>
          <Textarea
            id="education"
            name="education"
            value={formData.education}
            onChange={handleChange}
            rows={2}
          />
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={loading}>
          {loading ? "Creating..." : "Create Lead"}
        </Button>
      </div>
    </form>
  );
}
